import { useCallback, useMemo, useRef } from "react"

import { mapTree } from "shared/lib/treeUtils"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import {
  ProductPosition,
  TableProductPosition
} from "shared/types/ProductPosition"
import {
  ProductPositionAddonTypename,
  ProductPositionTypename,
  RoomTypename,
  isProjectProductPosition,
  isProjectProductPositionAddon,
  isProjectRoom
} from "shared/types/Project"
import { Room, TableRoom } from "shared/types/Room"
import { TreeNode } from "shared/types/TreeNode"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { TableAPI, TableCell } from "shared/ui/table/types"

import { ProductPositionTableProps } from "../ProductPositionTable"
import { ProductPositionTableRow } from "../ProductPositionTableRow"
import { useProductPositionAddonRowCells } from "./useProductPositionAddonRowCells"
import { useProductPositionRowCells } from "./useProductPositionRowCells"
import { useRoomRowCells } from "./useRoomRowCells"

function transformAddons(
  item: TableProductPosition | TableRoom | ProductPositionAddon
): TableProductPosition | TableRoom | ProductPositionAddon {
  if (isProjectProductPositionAddon(item) || isProjectRoom(item)) {
    return item
  }
  if (isProjectProductPosition(item)) {
    return {
      ...item,
      children: item.addons?.map(addon => ({
        ...addon
      }))
    }
  }
  return item
}

export function useProductPositionTableViewModel({
  projectID,
  activeTabID,
  isLoading,
  hideQuantity,
  showClarificationRequestColumn,
  columnPreset = "default",
  showSelectionCheckbox,
  isReadonly,
  projectChildren,
  selectedItems,
  RowRenderer,
  pendingProjectChildren,
  isSortingDisabled,
  onSelectionChange,
  ...rest
}: ProductPositionTableProps) {
  const tableAPIRef = useRef<TableAPI | null>(null)

  const projectChildrenWithAddons = useMemo(
    () =>
      mapTree(
        projectChildren as TreeNode<
          TableProductPosition | TableRoom | ProductPositionAddon
        >[],
        transformAddons
      ) || [],
    [projectChildren]
  )

  const rowContextValue = useMemo(
    () => ({
      hideQuantity,
      pendingProjectChildren,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset,
      projectID,
      isReadonly
    }),
    [
      hideQuantity,
      pendingProjectChildren,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset,
      projectID,
      isReadonly
    ]
  )

  const loadingRowContextValue = useMemo(
    () => ({
      hideQuantity,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset
    }),
    [
      hideQuantity,
      showClarificationRequestColumn,
      showSelectionCheckbox,
      columnPreset
    ]
  )

  const RowRendererComponent = RowRenderer || ProductPositionTableRow

  const mergedTableAPIRef = useMergedRefs(tableAPIRef, rest.tableAPIRef)
  const selectedItemCount = selectedItems?.length || 0

  const { cells: productPositionRowCells } = useProductPositionRowCells({
    hideQuantity,
    showClarificationRequestColumn,
    columnPreset
  })
  const { cells: productPositionAddonRowCells } =
    useProductPositionAddonRowCells({
      showClarificationRequestColumn
    })
  const { cells: roomRowCells } = useRoomRowCells({
    showClarificationRequestColumn,
    columnPreset
  })

  const getRowCells: (
    row: ProductPosition | Room | ProductPositionAddon
  ) => TableCell[] = useCallback(
    row => {
      switch (row.__typename) {
        case ProductPositionTypename:
          return productPositionRowCells
        case ProductPositionAddonTypename:
          return productPositionAddonRowCells
        case RoomTypename:
          return roomRowCells
        default:
          return []
      }
    },
    [productPositionRowCells, productPositionAddonRowCells, roomRowCells]
  )

  return {
    ...rest,
    activeTabID,
    isReadonly,
    isSortingDisabled: isSortingDisabled || selectedItemCount > 1,
    RowRenderer: RowRendererComponent,
    rowContextValue,
    loadingRowContextValue,
    tableAPIRef: mergedTableAPIRef,
    productPositions: projectChildrenWithAddons,
    isLoading,
    selectedItems,
    onSelectionChange,
    getRowCells
  }
}
